import { useEffect, useMemo, useState } from "react";
import { useCombinedStore } from "./useCombinedStore";

export interface BusinessProfile {
  id: string;
  name: string;
  businessName?: string;
  businessAddress?: string;
  logoURL?: string;
  phoneNumber?: string;
  role?: string;
  bio?: string;
  description?: string;
  businessType?: string;
  city?: string;
  country?: string;
}

type PublicBusinessesResponse = {
  success: boolean;
  data?: BusinessProfile[];
  message?: string;
};

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000/api/v1";

const resolveOwnerId = (item: any): string | null => {
  return (
    item?.userId || item?.ownerId || item?.businessId || item?.createdBy || null
  );
};

export const useBusinesses = () => {
  const [users, setUsers] = useState<BusinessProfile[]>([]);
  const [loading, setLoading] = useState(false);

  const combined = useCombinedStore() as any;

  const products = combined?.products || [];
  const hikes = combined?.hikes || [];
  const accommodations = combined?.accommodations || [];
  const carHire = combined?.carHire || combined?.cars || [];

  useEffect(() => {
    let mounted = true;

    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/public/businesses`);
        const result = (await response.json()) as PublicBusinessesResponse;

        if (!response.ok || !result.success) {
          throw new Error(result.message || "Failed to fetch businesses/users");
        }

        if (!mounted) return;

        const mapped: BusinessProfile[] = (result.data || []).map((user) => {
          return {
            id: user.id,
            name: user.name || "",
            businessName: user.businessName || "",
            businessAddress: user.businessAddress || "",
            logoURL: user.logoURL || "",
            phoneNumber: user.phoneNumber || "",
            role: user.role || "guest",
            bio: user.bio || "",
            description: user.description || "",
            businessType: user.businessType || "",
            city: user.city || "",
            country: user.country || "",
          };
        });

        setUsers(mapped);
      } catch (error) {
        console.error("Failed to fetch businesses/users:", error);

        if (mounted) {
          setUsers([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchUsers();

    return () => {
      mounted = false;
    };
  }, []);

  const activeOwnerIds = useMemo(() => {
    const allItems = [...products, ...hikes, ...accommodations, ...carHire];
    const ids = allItems.map(resolveOwnerId).filter(Boolean) as string[];
    return Array.from(new Set(ids));
  }, [products, hikes, accommodations, carHire]);

  const businesses = useMemo(() => {
    return users
      .filter((user) => activeOwnerIds.includes(user.id))
      .map((user) => ({
        ...user,
        title: user.businessName || user.name || "Business",
      }));
  }, [users, activeOwnerIds]);

  return {
    businesses,
    loading,
  };
};
